import { Link } from '@mui/material';
import type { GridRenderCellParams } from '@mui/x-data-grid-premium';
import type { ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import { SECURITY_ACTIONS } from '../../auth/Security';
import { H } from '../../Layout';
import { useDataLegacyRowPermissions } from '../hooks/useDataLegacyRowPermissions';
import { useDataRowPermissions } from '../hooks/useDataRowPermissions';
import { DataGridProps } from './types';

export const AUTOSIZE_MEASUREMENT_CLASSNAME = 'data-grid-autosize-measuring';

const CELL_SX = {
    gap: 0.5,
    height: '100%'
};

const AUTOSIZE_CELL_SX = {
    // Layout H defaults to width 100%, which blocks MUI autosize measurement.
    width: 'auto',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden'
};

const CLICKABLE_LINK_SX = {
    maxWidth: '100%',
    overflow: 'hidden',
    textAlign: 'inherit' as const
};

type CellContainerProps = Pick<
    DataGridProps,
    'clickableColumn' | 'isCellClickable' | 'feature' | 'entityName'
> & {
    params: GridRenderCellParams;
    children: ReactNode;
    onDetails?: (id: string | number, row) => void;
    onEdit?: (id: string | number, row) => void;
};

export function CellContainer({
    params,
    clickableColumn,
    isCellClickable,
    feature,
    entityName,
    onDetails,
    onEdit,
    children
}: CellContainerProps) {
    const legacyRowPermissions = useDataLegacyRowPermissions(feature, params.row.security);
    const centralizedRowPermissions = useDataRowPermissions(entityName, params.row.security);
    const rowPermissions = entityName ? centralizedRowPermissions : legacyRowPermissions;

    const canView = Boolean(onDetails && rowPermissions[SECURITY_ACTIONS.READ]);
    const canEdit = Boolean(onEdit && rowPermissions[SECURITY_ACTIONS.EDIT]);

    const isClickable = useMemo(() => {
        let isClickableCell = false;

        if (typeof isCellClickable === 'function') {
            isClickableCell = isCellClickable({
                ...params,
                field: params.field,
                columns: []
            });
        } else {
            isClickableCell = params.field === clickableColumn;
        }

        return isClickableCell;
    }, [clickableColumn, isCellClickable, params]);

    const handleClick = useCallback(() => {
        if (onDetails && canView) {
            onDetails(params.row.id, params.row);
            return;
        }

        if (onEdit && canEdit) {
            onEdit(params.row.id, params.row);
        }

        return;
    }, [canEdit, canView, onDetails, onEdit, params.row]);

    const isValidValue = typeof params.formattedValue === 'string' || typeof params.formattedValue === 'number';
    const cellSx = {
        ...CELL_SX,
        [`.${AUTOSIZE_MEASUREMENT_CLASSNAME} &`]: AUTOSIZE_CELL_SX
    };

    if (!isValidValue) {
        return null;
    }

    if (isClickable) {
        return (
            <H sx={cellSx}>
                {params.row.clickableStartAdornment}
                {canView || canEdit ? (
                    <Link
                        className="clickable"
                        color={'inherit'}
                        underline={'hover'}
                        component="button"
                        type="button"
                        onClick={handleClick}
                        sx={CLICKABLE_LINK_SX}
                    >
                        {children}
                    </Link>
                ) : (
                    children
                )}
                {params.row.clickableEndAdornment}
            </H>
        );
    }

    return <H sx={cellSx}>{children}</H>;
}
